import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { financialAPI } from '../../../services/apiServices';
import { useFeedback } from '../../../contexts/FeedbackContext';
import { Alert, Input, Modal, Select, Textarea } from '../../../components/common/UI';
import DateField from '../../../components/common/DateField';
import { ArrowUpIcon, EditIcon, PlusIcon, TrashIcon, TrendingIcon } from '../../../components/common/Icons';
import MoneyValue from '../../../components/common/MoneyValue';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { formatLocalizedDate, toDateInputValue, todayInputValue } from '../../../utils/dateUtils';
import { formatDecimalInput, formatDecimalValue, parseDecimalInput } from '../../../utils/currency';
import { requiredFieldMessage } from '../../../utils/validationMessages';

const createEmptyForm = () => ({
  id: null,
  type: 'credit',
  amount: '',
  description: '',
  launchDate: todayInputValue(),
  responsibleInvolvedId: '',
  responsibleName: '',
});
const getInvolvedName = (item) => item?.client?.name || item?.involvedName || '';
const getResponsibleName = (item) => getInvolvedName(item.responsibleInvolved) || item.responsibleName || '';
const recordTime = (value) => {
  const time = new Date(value || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};
const sortByCreatedDesc = (items = []) => (
  [...items].sort((a, b) => recordTime(b.createdAt || b.updatedAt || b.launchDate) - recordTime(a.createdAt || a.updatedAt || a.launchDate))
);

const FinancialTab = ({ businessId, data = [], involved = [], onRefresh }) => {
  const { t, i18n } = useTranslation();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(createEmptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [detail, setDetail] = useState(null);
  const { confirm, notify } = useFeedback();

  const typeOptions = [
    { value: 'credit', label: t('financial.credit') },
    { value: 'debit', label: t('financial.debit') },
  ];
  const involvedOptions = useMemo(() => [
    { value: '', label: t('progress.noResponsibleInvolved') },
    ...involved.map((person) => ({ value: person.id, label: getInvolvedName(person) || t('involved.manualPerson') })),
  ], [involved, t]);

  const rows = useMemo(() => sortByCreatedDesc(data), [data]);
  const credits = rows.filter((entry) => entry.type === 'credit').reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  const debits = rows.filter((entry) => entry.type === 'debit').reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  const balance = credits - debits;
  const locale = i18n.language === 'en' ? 'en-US' : i18n.language === 'es' ? 'es-ES' : 'pt-BR';

  const openCreate = () => {
    setForm(createEmptyForm());
    setFieldErrors({});
    setError('');
    setModal(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      type: item.type || 'credit',
      amount: formatDecimalValue(item.amount, i18n.language),
      description: item.description || '',
      launchDate: toDateInputValue(item.launchDate),
      responsibleInvolvedId: item.responsibleInvolvedId || item.responsibleInvolved?.id || '',
      responsibleName: item.responsibleName || '',
    });
    setFieldErrors({});
    setError('');
    setModal(true);
  };

  const handleSave = async () => {
    const amount = parseDecimalInput(form.amount);
    if (String(form.amount).trim() === '') {
      const message = requiredFieldMessage(t, t('financial.amountLabel'));
      setFieldErrors({ amount: message });
      setError(message);
      return;
    }
    if (!amount || amount <= 0) {
      setFieldErrors({ amount: t('financial.invalidAmount') });
      setError(t('financial.invalidAmount'));
      return;
    }
    setFieldErrors({});

    setSaving(true);
    setError('');
    const payload = {
      type: form.type,
      amount,
      description: form.description,
      responsibleInvolvedId: form.responsibleInvolvedId || null,
      responsibleName: form.responsibleName.trim() || null,
      ...(form.launchDate ? { launchDate: form.launchDate } : {}),
    };

    try {
      if (form.id) await financialAPI.update(businessId, form.id, payload);
      else await financialAPI.create(businessId, payload);
      setModal(false);
      setForm(createEmptyForm());
      onRefresh();
    } catch (err) {
      setError(getApiErrorMessage(err, t('financial.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const deleteDescription = (item) => {
    const date = formatLocalizedDate(item.launchDate, locale) || t('common.date');
    const type = item.type === 'credit' ? t('financial.credit') : t('financial.debit');
    return t('financial.deleteDetailMessage', {
      detail: `${type} - ${item.description || t('common.description')} - ${date}`,
    });
  };

  const handleDelete = async (item) => {
    const ok = await confirm({
      variant: 'danger',
      title: t('financial.deleteTitle'),
      message: deleteDescription(item),
      confirmText: t('common.delete'),
    });
    if (!ok) return;
    try {
      await financialAPI.remove(businessId, item.id);
      onRefresh();
    } catch {
      notify({ variant: 'danger', title: t('financial.deleteErrorTitle'), message: t('financial.deleteError') });
    }
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-soft">
        <p className="text-sm text-slate-600">{t('financial.currentBalance')}</p>
        <p className={`mt-2 text-2xl font-bold ${balance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          <MoneyValue value={balance} />
        </p>
      </section>

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
        <div className="flex min-h-16 items-center justify-between gap-4 border-b border-slate-200 px-4 py-3">
          <h2 className="font-bold text-slate-900">{t('financial.entries')}</h2>
          <button className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-sky-500 px-5 text-sm font-bold text-white shadow-soft transition hover:bg-sky-600" onClick={openCreate} type="button">
            <PlusIcon className="h-4 w-4" />
            {t('common.new')}
          </button>
        </div>

        {rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500">{t('financial.empty')}</p>
        ) : (
          <div className="divide-y divide-slate-200">
            {rows.map((item) => {
              const isCredit = item.type === 'credit';
              return (
                <div key={item.id} className="flex min-h-[76px] cursor-pointer items-center gap-4 px-4 py-3 transition hover:bg-slate-50" onClick={() => setDetail(item)} role="button" tabIndex={0}>
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isCredit ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                    {isCredit ? <TrendingIcon className="h-5 w-5" /> : <ArrowUpIcon className="h-5 w-5 rotate-90" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold text-slate-900">{item.description || (isCredit ? t('financial.credit') : t('financial.debit'))}</p>
          <p className="mt-1 text-sm text-slate-600">{formatLocalizedDate(item.launchDate, locale)}</p>
                    {getResponsibleName(item) && (
                      <p className="mt-1 text-xs font-semibold text-slate-500">{t('businessDetails.by', { name: getResponsibleName(item) })}</p>
                    )}
                  </div>
                  <p className={`font-bold ${isCredit ? 'text-emerald-600' : 'text-red-500'}`}>
                    {isCredit ? '+' : '-'}<MoneyValue value={item.amount} />
                  </p>
                  <div className="flex gap-1">
                    <button className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100" onClick={(event) => { event.stopPropagation(); openEdit(item); }} type="button" aria-label={t('financial.editEntry')}>
                      <EditIcon className="h-4 w-4" />
                    </button>
                    <button className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-500 hover:bg-red-50" onClick={(event) => { event.stopPropagation(); handleDelete(item); }} type="button" aria-label={t('financial.deleteEntry')}>
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Modal isOpen={modal} onClose={() => setModal(false)} title={form.id ? t('financial.editEntry') : t('financial.newEntry')} size="sm">
        {error && <Alert variant="error" onClose={() => setError('')}>{error}</Alert>}
        <div className="mt-4 space-y-4">
          <Select label={t('common.type')} value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })} options={typeOptions} />
          <Input label={t('financial.amountLabel')} inputMode="decimal" value={form.amount} onChange={(event) => { setForm({ ...form, amount: formatDecimalInput(event.target.value, i18n.language) }); setFieldErrors((current) => ({ ...current, amount: '' })); }} placeholder={t('financial.amountPlaceholder')} helpText={t('financial.amountHelp')} error={fieldErrors.amount} />
          <DateField label={t('common.date')} value={form.launchDate} onChange={(launchDate) => setForm({ ...form, launchDate })} helpText={t('common.dateHelp')} />
          <Select label={t('progress.responsibleInvolved')} value={form.responsibleInvolvedId} onChange={(event) => setForm({ ...form, responsibleInvolvedId: event.target.value })} options={involvedOptions} />
          <Input label={t('progress.responsibleName')} value={form.responsibleName} onChange={(event) => setForm({ ...form, responsibleName: event.target.value })} placeholder={t('progress.responsibleNamePlaceholder')} helpText={t('progress.responsibleHelp')} />
          <Textarea label={t('common.description')} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} rows={3} />
          <div className="flex gap-2">
            <button className="inline-flex h-10 items-center justify-center rounded-lg bg-sky-500 px-5 text-sm font-bold text-white disabled:opacity-60" onClick={handleSave} disabled={saving} type="button">
              {saving ? t('common.saving') : t('common.save')}
            </button>
            <button className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-5 text-sm font-bold text-slate-900" onClick={() => setModal(false)} type="button">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </Modal>
      <Modal isOpen={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.description || t('financial.entries')} size="sm">
        {detail && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Info label={t('common.type')} value={detail.type === 'credit' ? t('financial.credit') : t('financial.debit')} />
            <Info
              label={t('financial.amountLabel')}
              value={<span className={detail.type === 'debit' ? 'text-red-500' : 'text-emerald-600'}><MoneyValue value={detail.amount} /></span>}
            />
            <Info label={t('common.date')} value={formatLocalizedDate(detail.launchDate, locale) || '-'} />
            <Info label={t('progress.responsibleName')} value={getResponsibleName(detail) || '-'} />
            <Info className="sm:col-span-2" label={t('common.description')} value={detail.description || '-'} />
          </div>
        )}
      </Modal>
    </div>
  );
};

const Info = ({ label, value, className = '' }) => (
  <div className={`rounded-lg bg-slate-50 p-3 ${className}`}>
    <p className="text-xs font-bold text-slate-500">{label}</p>
    <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
  </div>
);

export default FinancialTab;
